import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { X, ChevronDown, ChevronRight, AlertCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { sinceChartStart } from "@/lib/chartWindow";

const usd = (n, dec = 2) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const amt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 6 });
const TONE = {
  ca_community: { color: "#7C3AED", bg: "rgba(124,58,237,0.10)" },
  bot_community: { color: "#15DCAC", bg: "rgba(21,220,172,0.12)" },
  staking: { color: "#C2185B", bg: "rgba(194,24,91,0.10)" },
};

function Row({ c, tone, parentKey }) {
  const [open, setOpen] = useState(false);
  const series = useMemo(() => sinceChartStart(c.series || []), [c.series]);
  const canOpen = series.length > 0 || (c.records || []).length > 0;
  // Nothing earned reads as a dash, never as a $0 that looks like a real number.
  const earned = (c.amount || 0) > 0 || (c.usd || 0) > 0;
  return (
    <div className="border-t first:border-t-0" style={{ borderColor: "rgba(15,18,38,0.06)" }}
      data-testid={`breakdown-row-${c.key}`}>
      <div className="flex items-center gap-3 py-2.5 flex-wrap">
        <button type="button" disabled={!canOpen} onClick={() => setOpen((v) => !v)}
          data-testid={`breakdown-toggle-${c.key}`}
          className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${canOpen ? "hover:bg-black/5" : "opacity-25 cursor-default"}`}>
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <div className="min-w-[210px] flex-1">
          <div className="text-[13px] font-medium text-ink">{c.label}</div>
          {earned ? (
            <div className="text-[11px] text-ink-3 font-mono">
              {amt(c.amount)} {c.token}
              {c.records_total ? ` · ${c.records_total} record${c.records_total === 1 ? "" : "s"}` : " · running total"}
            </div>
          ) : (
            <div className="text-[11px] flex items-center gap-1" style={{ color: c.readable ? "#64748B" : "#B45309" }}
              data-testid={`breakdown-unreadable-${c.key}`}>
              <AlertCircle size={11} /> {c.note || (c.readable ? "Nothing earned here yet" : "Can't be read from CaryPact yet")}
            </div>
          )}
        </div>
        <div className="w-28 shrink-0">
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(15,18,38,0.07)" }}>
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, c.share || 0)}%`, background: tone.color }} />
          </div>
          <div className="text-[10px] font-mono text-ink-3 mt-0.5">{earned ? `${c.share}% of the tile` : "—"}</div>
        </div>
        <div className="w-28 text-right font-mono font-semibold tabular-nums shrink-0"
          style={{ color: earned ? undefined : "#94A3B8" }} data-testid={`breakdown-usd-${c.key}`}>
          {earned ? usd(c.usd, 2) : "—"}
        </div>
      </div>

      {open && canOpen && (
        <div className="pb-4 pl-8 space-y-3" data-testid={`breakdown-detail-${c.key}`}>
          {series.length > 2 && (
            <div style={{ width: "100%", height: 130 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={130}>
                <BarChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap={2}>
                  <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} minTickGap={26} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} width={50} tickLine={false} axisLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${Math.round(v)}`)} />
                  <Tooltip formatter={(v) => [usd(v), "USD"]} labelFormatter={(d) => d} cursor={{ fill: "rgba(15,18,38,0.04)" }}
                    contentStyle={{ background: "rgba(255,255,255,0.96)", border: "1px solid rgba(15,18,38,0.1)", borderRadius: 10, fontSize: 12 }} />
                  {/* Payouts land on their own days — bars, not a curve that invents the days in between. */}
                  <Bar dataKey="usd" radius={[3, 3, 0, 0]} maxBarSize={22}>
                    {series.map((s) => <Cell key={s.date} fill={tone.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {(c.records || []).length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid={`breakdown-records-${c.key}`}>
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider font-mono text-ink-3">
                    <th className="text-left py-1">Date</th>
                    <th className="text-right py-1">Amount</th>
                    <th className="text-right py-1">USD</th>
                    <th className="text-left py-1 pl-4">{c.records.some((r) => r.source) ? "Type" : "From"}</th>
                  </tr>
                </thead>
                <tbody>
                  {c.records.map((r, i) => (
                    <tr key={`${r.date}-${i}`} className="border-t" style={{ borderColor: "rgba(15,18,38,0.04)" }}>
                      <td className="py-1 font-mono text-ink-3">{r.date}</td>
                      <td className="py-1 text-right font-mono tabular-nums">{amt(r.amount)} {r.token}</td>
                      <td className="py-1 text-right font-mono tabular-nums font-semibold">{usd(r.usd)}</td>
                      <td className="py-1 pl-4 text-ink-3 font-mono">{r.source || r.peer || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {c.records_total > c.records.length && (
                <div className="text-[10px] text-ink-3 font-mono mt-1">
                  showing the latest {c.records.length} of {c.records_total}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * What makes up a community tile. Opens under the tile row so the dashboard stays
 * calm until someone wants to dig — every component, what it's worth, its share of
 * the parent, and the records behind it.
 */
export default function StreamBreakdown({ portfolioId, parentKey, onClose, tileLabel, tileUsd }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setData(null); setError("");
    api.get(`/portfolios/${portfolioId}/reward-streams`)
      .then(({ data }) => { if (live) setData(data); })
      .catch((e) => { if (live) setError(e?.response?.data?.detail || "Could not load the breakdown"); });
    return () => { live = false; };
  }, [portfolioId]);

  const parent = (data?.parents || []).find((p) => p.key === parentKey);
  const tone = TONE[parentKey] || TONE.ca_community;
  // If the family and its tile ever disagree, say so rather than let two numbers
  // sit next to each other looking wrong.
  const gap = parent && tileUsd != null ? Math.abs(tileUsd - parent.usd) : 0;
  const sub = !parent
    ? "every reward inside this tile · straight from CaryPact"
    : gap > Math.max(2, (tileUsd || 0) * 0.005)
      ? `${parent.components.length} components · ${usd(parent.usd, 0)} of the ${usd(tileUsd, 0)} on the tile`
      : `the whole ${tileLabel || "tile"} · straight from CaryPact`;

  return (
    <div className="glass p-5 mb-3 fade-up" data-testid={`stream-breakdown-${parentKey}`}>
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: tone.bg }}>
          <ChevronDown size={16} style={{ color: tone.color }} />
        </div>
        <div className="min-w-0">
          <h3 className="font-display text-lg font-semibold tracking-tight leading-tight">
            {parent?.label || tileLabel || "Breakdown"} breakdown
          </h3>
          <div className="kpi-label mt-0.5">{sub}</div>
        </div>
        {parent && (
          <div className="ml-auto text-right">
            <div className="font-display text-xl font-bold tabular-nums" style={{ color: tone.color }}
              data-testid={`breakdown-total-${parentKey}`}>{usd(parent.usd, 2)}</div>
            <div className="text-[10px] font-mono text-ink-3">{parent.components.length} components</div>
          </div>
        )}
        <button type="button" onClick={onClose} data-testid={`breakdown-close-${parentKey}`}
          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-black/5 shrink-0" title="Close breakdown">
          <X size={15} />
        </button>
      </div>

      {error && <div className="chip chip-amber mt-3" data-testid="breakdown-error">{error}</div>}
      {!data && !error && (
        <div className="flex items-center gap-2 text-sm text-ink-3 py-6 font-mono" data-testid="breakdown-loading">
          <Loader2 size={14} className="animate-spin" /> Loading breakdown…
        </div>
      )}
      {parent && (
        <div className="mt-2">
          {parent.components.map((c) => <Row key={c.key} c={c} tone={tone} parentKey={parentKey} />)}
        </div>
      )}
    </div>
  );
}
